import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpContext } from '@angular/common/http';
import { BehaviorSubject, EMPTY, Observable, Subject, of, timer } from 'rxjs';
import { catchError, concatMap, exhaustMap, filter, map, switchMap, take, takeWhile, tap } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { environment } from '../../../environments/environment';
import { MembershipStatus } from '../../shared/interfaces/membership.interface';
import { REQUIRE_AUTH } from '../../shared/interceptors/auth-context';
import { MembershipService as SharedMembershipService, BusinessMembershipDto } from '../../shared/services/membership.service';
import { GracePeriodService } from '../../shared/services/grace-period.service';

/** How often, and how many times, to look for the membership the payment webhook updates */
const PAYMENT_SYNC_INTERVAL_MS = 3000;
const PAYMENT_SYNC_MAX_ATTEMPTS = 20;

/**
 * How pressing the end of the 7-day free trial is, so every trial notice warms up the same
 * way: calm in its first days, yellow with 3 or 4 days left, orange with 2 and red on the last day.
 */
export type TrialTone = 'calm' | 'soon' | 'urgent' | 'last' | 'expired';

export function trialToneFor(daysLeft: number | null, expired = false): TrialTone {
  if (expired) return 'expired';
  if (daysLeft === null || daysLeft > 4) return 'calm';
  if (daysLeft > 2) return 'soon';
  if (daysLeft > 1) return 'urgent';
  return 'last';
}

@Injectable({ providedIn: 'root' })
export class MembershipService {
  private http = inject(HttpClient);
  private baseUrl = environment.apiUrl;
  private sharedMembershipService = inject(SharedMembershipService);
  private gracePeriodService = inject(GracePeriodService);

  // State management
  private membershipStatusSubject = new BehaviorSubject<MembershipStatus | null>(null);
  private trialDaysRemainingSubject = new BehaviorSubject<number | null>(null);
  private isExpiredSubject = new BehaviorSubject<boolean>(false);
  private expiredMembershipLockSubject = new BehaviorSubject<boolean>(false);
  private loadingSubject = new BehaviorSubject<boolean>(false);

  // Public observables
  membershipStatus$ = this.membershipStatusSubject.asObservable();
  trialDaysRemaining$ = this.trialDaysRemainingSubject.asObservable();
  isExpired$ = this.isExpiredSubject.asObservable();
  loading$ = this.loadingSubject.asObservable();

  /** Same state as signals, for templates and computed values */
  readonly status = toSignal(this.membershipStatus$, { initialValue: null });
  readonly trialDaysLeft = toSignal(this.trialDaysRemaining$, { initialValue: null });

  private paymentSyncSubject = new Subject<string | undefined>();
  private membershipUpdatedSubject = new Subject<BusinessMembershipDto | null>();
  /** Emits when a payment changed the membership, so pages showing it can reload */
  membershipUpdated$ = this.membershipUpdatedSubject.asObservable();

  constructor() {
    // Don't load membership status automatically - let components request it when needed

    // Lives here rather than in the billing page so it keeps going if the user navigates away
    this.paymentSyncSubject
      .pipe(switchMap((refPayco) => this.pollUntilPaymentApplied(refPayco)))
      .subscribe();
  }

  /**
   * Call when the user comes back from checkout. The membership is updated by the
   * payment webhook, which can arrive some seconds later, so the status is refreshed
   * now and again as soon as the membership changes: every banner shows the new
   * state without having to navigate.
   * @param refPayco ePayco reference of the payment, stored as the membership's external id
   */
  syncAfterPayment(refPayco?: string): void {
    this.paymentSyncSubject.next(refPayco);
  }

  private pollUntilPaymentApplied(refPayco?: string): Observable<unknown> {
    const businessId = this.getBusinessId();
    if (!businessId) return EMPTY;

    let baseline: string | undefined;
    return timer(0, PAYMENT_SYNC_INTERVAL_MS).pipe(
      take(PAYMENT_SYNC_MAX_ATTEMPTS),
      // A failed read is skipped, never mistaken for "no membership"
      exhaustMap(() =>
        this.sharedMembershipService
          .getLatestBusinessMembership(businessId)
          .pipe(map((r) => r.businessMembership ?? null), catchError(() => EMPTY))
      ),
      map((membership) => {
        const current = this.membershipFingerprint(membership);
        const isFirst = baseline === undefined;
        if (isFirst) baseline = current;
        const applied =
          (!!refPayco && membership?.externalMembershipId === refPayco) || current !== baseline;
        return { membership, isFirst, applied };
      }),
      takeWhile((r) => !r.applied, true),
      // First read shows what is stored now; the change is shown as soon as it lands
      filter((r) => r.isFirst || r.applied),
      concatMap((r) =>
        this.getMembershipStatus().pipe(
          tap(() => {
            if (!r.applied) return;
            this.gracePeriodService.checkGracePeriodStatus().pipe(catchError(() => EMPTY)).subscribe();
            this.membershipUpdatedSubject.next(r.membership);
          })
        )
      )
    );
  }

  /** What a payment can change in a membership */
  private membershipFingerprint(m: BusinessMembershipDto | null): string {
    if (!m) return 'none';
    return [
      m.businessMembershipId,
      m.membershipStatusId,
      m.endDate,
      m.nextBillingDate,
      m.trialEndDate,
      m.updatedAt,
      m.numberOfProfessionals,
      m.paidProfessionalsCount,
      (m.additionalFeatures ?? []).map((f) => `${f.membershipPlanFeatureId}:${f.quantity}`).join(','),
    ].join('|');
  }

  /**
   * Get current membership status from the backend
   */
  getMembershipStatus(): Observable<MembershipStatus> {
    this.loadingSubject.next(true);
    return this.http.get<MembershipStatus>(`${this.baseUrl}/api/v1/membership/status`, {
      context: new HttpContext().set(REQUIRE_AUTH, true)
    }).pipe(
      tap(status => {
        this.membershipStatusSubject.next(status);
        this.trialDaysRemainingSubject.next(status.trialDaysRemaining);
        this.isExpiredSubject.next(status.isExpired);
        this.loadingSubject.next(false);
      }),
      catchError(error => {
        console.error('Error fetching membership status:', error);
        this.loadingSubject.next(false);
        // Return default expired status on error
        const defaultStatus: MembershipStatus = {
          status: 'expired',
          trialDaysRemaining: null,
          trialEndDate: null,
          isExpired: true,
          planName: 'Unknown',
          nextBillingDate: null
        };
        this.membershipStatusSubject.next(defaultStatus);
        this.isExpiredSubject.next(true);
        return of(defaultStatus);
      })
    );
  }

  /**
   * Check membership status and update internal state
   */
  checkMembershipStatus(): void {
    this.getMembershipStatus().subscribe();
  }


  /**
   * Get current membership status from cache
   */
  getCurrentMembershipStatus(): MembershipStatus | null {
    return this.membershipStatusSubject.value;
  }

  /**
   * Check if user has valid membership (not expired)
   */
  hasValidMembership(): boolean {
    const status = this.membershipStatusSubject.value;
    return status ? !status.isExpired : false;
  }

  /**
   * Check if user is in trial period
   */
  isInTrial(): boolean {
    const status = this.membershipStatusSubject.value;
    return status ? status.status === 'trial' && !status.isExpired : false;
  }

  /**
   * Get trial days remaining
   */
  getTrialDaysRemaining(): number | null {
    return this.trialDaysRemainingSubject.value;
  }

  /**
   * Check if membership is expired
   */
  isMembershipExpired(): boolean {
    return this.isExpiredSubject.value;
  }

  /**
   * Refresh membership status from server
   */
  refreshMembershipStatus(): Observable<MembershipStatus> {
    return this.getMembershipStatus();
  }

  /**
   * Check if membership status is currently loading
   */
  isLoading(): boolean {
    return this.loadingSubject.value;
  }

  /**
   * Membership interceptor lock used to block protected API calls
   * after receiving 402 responses.
   */
  hasExpiredMembershipLock(): boolean {
    return this.expiredMembershipLockSubject.value;
  }

  setExpiredMembershipLock(isLocked: boolean): void {
    this.expiredMembershipLockSubject.next(isLocked);
  }

  /**
   * Reset membership state when user logs out to avoid leaking
   * membership status between different sessions.
   */
  resetMembershipState(): void {
    this.membershipStatusSubject.next(null);
    this.trialDaysRemainingSubject.next(null);
    this.isExpiredSubject.next(false);
    this.expiredMembershipLockSubject.next(false);
    this.loadingSubject.next(false);
    this.sharedMembershipService.clearCache();
  }

  /**
   * Get business ID from JWT token stored in localStorage
   * This avoids circular dependency with AuthService by reading the token directly
   */
  private getBusinessId(): string | null {
    try {
      const authData = localStorage.getItem('auth_data');
      if (!authData) {
        return null;
      }

      const parsed = JSON.parse(authData);
      if (!parsed.token) {
        return null;
      }

      // Decode JWT token to get business_id
      // JWT format: header.payload.signature
      const parts = parsed.token.split('.');
      if (parts.length !== 3) {
        return null;
      }

      // Decode base64 payload
      const payload = JSON.parse(atob(parts[1]));
      return payload.business_id || null;
    } catch (error) {
      // If token parsing fails, return null
      return null;
    }
  }

  /**
   * Check if user has access to a specific feature
   * @param featureName The name of the feature to check (e.g., "Promociones y campañas")
   * @returns true if the feature is available in the latest membership
   */
  hasFeature(featureName: string): boolean {
    const businessId = this.getBusinessId();
    if (!businessId) {
      return false;
    }

    // Check cached membership first
    const cachedMembership = this.sharedMembershipService.getCachedMembership();
    if (cachedMembership) {
      return this.checkFeatureInMembership(cachedMembership, featureName);
    }

    // If not cached, return false (will be checked async when membership loads)
    return false;
  }

  /**
   * Check if user has access to a specific feature (async)
   * Loads membership if not cached
   * @param featureName The name of the feature to check
   * @returns Observable<boolean>
   */
  hasFeatureAsync(featureName: string): Observable<boolean> {
    const businessId = this.getBusinessId();
    if (!businessId) {
      return of(false);
    }

    // Check cached membership first
    const cachedMembership = this.sharedMembershipService.getCachedMembership();
    if (cachedMembership) {
      return of(this.checkFeatureInMembership(cachedMembership, featureName));
    }

    // Load membership if not cached
    return this.sharedMembershipService.getCachedLatestMembership(businessId).pipe(
      map(membership => {
        if (!membership) {
          return false;
        }
        return this.checkFeatureInMembership(membership, featureName);
      }),
      catchError(() => of(false))
    );
  }

  /**
   * Get list of available feature names from the latest membership
   * @returns Array of feature names
   */
  getAvailableFeatures(): string[] {
    const businessId = this.getBusinessId();
    if (!businessId) {
      return [];
    }

    const cachedMembership = this.sharedMembershipService.getCachedMembership();
    if (!cachedMembership) {
      return [];
    }

    return cachedMembership.additionalFeatures.map(f => f.featureName);
  }

  /**
   * Get list of available feature names (async)
   * Loads membership if not cached
   * @returns Observable<string[]>
   */
  getAvailableFeaturesAsync(): Observable<string[]> {
    const businessId = this.getBusinessId();
    if (!businessId) {
      return of([]);
    }

    const cachedMembership = this.sharedMembershipService.getCachedMembership();
    if (cachedMembership) {
      return of(cachedMembership.additionalFeatures.map(f => f.featureName));
    }

    return this.sharedMembershipService.getCachedLatestMembership(businessId).pipe(
      map(membership => {
        if (!membership) {
          return [];
        }
        return membership.additionalFeatures.map(f => f.featureName);
      }),
      catchError(() => of([]))
    );
  }

  /**
   * Get the number of paid professionals from the latest membership
   * @returns Observable<number> - Total professionals paid (includes base 1)
   */
  getPaidProfessionalsCount(): Observable<number> {
    const businessId = this.getBusinessId();
    if (!businessId) {
      return of(1); // Default to 1 if no business ID
    }

    const cachedMembership = this.sharedMembershipService.getCachedMembership();
    if (cachedMembership) {
      return of(this.resolvePaidProfessionalsCount(cachedMembership));
    }

    return this.sharedMembershipService.getCachedLatestMembership(businessId).pipe(
      map(membership => {
        return this.resolvePaidProfessionalsCount(membership);
      }),
      catchError(() => of(1))
    );
  }

  /**
   * Get days until membership renewal
   * Uses trialEndDate for trial memberships, nextBillingDate for active memberships
   * @returns Observable<number> - Days until renewal (0 if expired or no date)
   */
  getDaysUntilRenewal(): Observable<number> {
    const businessId = this.getBusinessId();
    if (!businessId) {
      return of(0);
    }

    const cachedMembership = this.sharedMembershipService.getCachedMembership();
    if (cachedMembership) {
      return of(this.calculateDaysUntilRenewal(cachedMembership));
    }

    return this.sharedMembershipService.getCachedLatestMembership(businessId).pipe(
      map(membership => {
        if (!membership) {
          return 0;
        }
        return this.calculateDaysUntilRenewal(membership);
      }),
      catchError(() => of(0))
    );
  }

  /**
   * Calculate days until renewal from membership data
   */
  private calculateDaysUntilRenewal(membership: BusinessMembershipDto): number {
    const now = new Date();
    let renewalDate: Date | null = null;

    // For trial memberships, use trialEndDate
    if (membership.membershipStatus.name.toLowerCase() === 'trial' && membership.trialEndDate) {
      renewalDate = new Date(membership.trialEndDate);
    }
    // For active memberships, use nextBillingDate or endDate
    else if (membership.membershipStatus.name.toLowerCase() === 'active') {
      if (membership.nextBillingDate) {
        renewalDate = new Date(membership.nextBillingDate);
      } else if (membership.endDate) {
        renewalDate = new Date(membership.endDate);
      }
    }

    if (!renewalDate) {
      return 0;
    }

    const diffTime = renewalDate.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return Math.max(0, diffDays); // Return 0 if already expired
  }

  /**
   * Resolve paid professionals count with fallbacks when backend fields are stale.
   * - paidProfessionalsCount: preferred
   * - numberOfProfessionals: total professionals (includes base 1)
   * - membershipPlan.numberOfProfessionals: capacity the plan itself grants
   * - "Múltiples profesionales" feature quantity: treated as additional, so +1
   */
  private resolvePaidProfessionalsCount(
    membership: BusinessMembershipDto | null
  ): number {
    if (!membership) {
      return 1;
    }

    const paidCount = membership.paidProfessionalsCount ?? 0;
    const totalFromMembership = membership.numberOfProfessionals ?? 0;
    const totalFromPlan = membership.membershipPlan?.numberOfProfessionals ?? 0;
    const multiProfessionalsFeature = membership.additionalFeatures?.find(
      (feature) => feature.featureName === 'Múltiples profesionales'
    );
    const totalFromFeature =
      (multiProfessionalsFeature?.quantity ?? 0) > 0
        ? (multiProfessionalsFeature?.quantity ?? 0) + 1
        : 0;

    const resolved = Math.max(paidCount, totalFromMembership, totalFromPlan, totalFromFeature);
    return resolved > 0 ? resolved : 1;
  }

  /**
   * Calculate full price for a professional (no proportional pricing)
   * @param daysRemaining Number of days until renewal (kept for compatibility but not used)
   * @param monthlyPrice Monthly price per professional (default: 20000)
   * @returns Full monthly price
   */
  calculateProportionalProfessionalPrice(daysRemaining: number, monthlyPrice: number = 20000): number {
    // Always return full monthly price (no proportional calculation)
    return monthlyPrice;
  }

  /**
   * Calculate full price for an additional feature (no proportional pricing)
   * @param daysRemaining Number of days until renewal (kept for compatibility but not used)
   * @param monthlyPrice Monthly price for the feature
   * @param isAnnual Whether the billing cycle is annual
   * @returns Full period price
   */
  calculateProportionalFeaturePrice(daysRemaining: number, monthlyPrice: number, isAnnual: boolean = false): number {
    // Always return full period price (no proportional calculation)
    return isAnnual ? monthlyPrice * 12 * 0.85 : monthlyPrice;
  }

  /**
   * Get price per professional from membership plan
   * @returns Observable<number> - Price per professional monthly (default: 20000)
   */
  getPricePerProfessional(): Observable<number> {
    const businessId = this.getBusinessId();
    if (!businessId) {
      return of(20000); // Default price
    }

    const cachedMembership = this.sharedMembershipService.getCachedMembership();
    if (cachedMembership?.membershipPlan?.pricePerProfessional) {
      return of(cachedMembership.membershipPlan.pricePerProfessional);
    }

    return this.sharedMembershipService.getCachedLatestMembership(businessId).pipe(
      map(membership => {
        if (!membership?.membershipPlan?.pricePerProfessional) {
          return 20000; // Default price
        }
        return membership.membershipPlan.pricePerProfessional;
      }),
      catchError(() => of(20000))
    );
  }

  /**
   * Internal helper to check if a feature exists in a membership
   */
  private checkFeatureInMembership(
    membership: BusinessMembershipDto,
    featureName: string
  ): boolean {
    // Check if membership is expired
    const now = new Date();
    const endDate = membership.endDate ? new Date(membership.endDate) : null;
    const trialEndDate = membership.trialEndDate ? new Date(membership.trialEndDate) : null;

    const status = membership.membershipStatus.name.toLowerCase();

    // Check if membership is explicitly expired or cancelled
    if (status === 'expired' || status === 'cancelled') {
      return false;
    }

    // Trial memberships get access to all features while active
    if (status === 'trial') {
      if (trialEndDate && trialEndDate < now) {
        return false;
      }
      return true;
    }

    // Check if active membership expired
    if (status === 'active' && endDate) {
      if (endDate < now) {
        return false;
      }
    }

    // Check if feature is included in the plan
    if (membership.membershipPlan.includedFeatureNames?.includes(featureName)) {
      return true;
    }

    // Check if feature exists in additional (purchased) features
    return (membership.additionalFeatures ?? []).some(
      feature => feature.featureName === featureName
    );
  }
}
